import { useEffect, useState } from 'react';
import { Box } from "@mui/system";
import DerivativesStrategiesTable from "./DerivativesStrategiesTable";
import { Card, Paper, Slider, Typography } from "@mui/material";
import CalculateIcon from '@mui/icons-material/Calculate';
import HistoryIcon from '@mui/icons-material/History';
import { COLORS } from "../../../ui/Theme";
import { useStrategiesContext } from "../../../context/derivatives/strategies/DerivativesStrategiesContext";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import DerivativesStrategiesDetailsAction from "./strategies-details/DerivativesStrategiesDetailsAction";
import Grid2 from "@mui/material/Unstable_Grid2";
import DerivativesStrategiesDetailsChart from "./strategies-details/DerivativesStrategiesDetailsChart";
import {
    calculateProfitChartByPercent,
    calculateProfitChartByPrice
} from "./strategies-details/DerivativesStrategiesDetailsFormule";
import { Link, useLocation } from "react-router-dom";
import useResponsive from "../../../components/hooks/useResponsive";
import DerivativesStrategiesSymbols from "./DerivativesStrategiesSymbols";

const DerivativesStrategies = () => {
    const {
        selectedRowDetails,
        setSelectedRowDetails,
        chartData,
        setChartData,
    } = useStrategiesContext();

    const { isMd } = useResponsive();
    const { pathname } = useLocation();
    const [chartAccuracy, setChartAccuracy] = useState(10);

    // handle change chart accuray slider
    const handleSliderChange = (event, newValue) => {
        setChartAccuracy(newValue);
    };

    useEffect(() => {
        if (selectedRowDetails.coordinates.length > 0) {
            const priceData = calculateProfitChartByPrice(
                selectedRowDetails.coordinates,
                chartAccuracy
            );
            setChartData((prev) => ({ ...prev, priceChartData: priceData }));

            const percentData =
                selectedRowDetails.profit_factor !== null
                    ? calculateProfitChartByPercent(priceData, selectedRowDetails.profit_factor)
                    : [];
            setChartData((prev) => ({ ...prev, percentChartData: percentData }));
        } else {
            setChartData({ priceChartData: [], percentChartData: [] });
        }
    }, [selectedRowDetails.coordinates, chartAccuracy]);

    // clear selectedRowDetails when pathname changed
    useEffect(() => {
        setSelectedRowDetails({
            actions: [],
            coordinates: [],
            profit_factor: null,
            base_equity_last_price: null,
        });
    }, [pathname])

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {/*symbols and calculator and history*/}
            <Paper>
                <Grid2 container spacing={2}>
                    {/*symbols and buttons*/}
                    <Grid2 xs={12}>
                        <Card>
                            <Grid2 container spacing={2} flexDirection={!isMd ? "column-reverse" : "row"}>
                                {/* symbols */}
                                <Grid2 xs={12} md={8.5} lg={9.5}>
                                    <DerivativesStrategiesSymbols />
                                </Grid2>

                                {/*calculator and history*/}
                                <Grid2 xs={12} md={3.5} lg={2.5} sx={{ width: "100%" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            gap: "10px",
                                            width: "100%",
                                        }}
                                    >
                                        <Link
                                            to="/dashboard/derivatives/calculator"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: "4px",
                                                border: "1px solid #EEEEEE",
                                                borderRadius: "12px",
                                                padding: "12px 8px",
                                                backgroundColor: COLORS.WHITE,
                                                cursor: "pointer",
                                                width: isMd ? "100px" : "100%",
                                            }}
                                        >
                                            <Typography sx={{ fontSize: "10px", fontWeight: 900, color: COLORS.BLACK }}>
                                                ماشین حساب
                                            </Typography>
                                            <CalculateIcon sx={{ color: COLORS.BLACK }} />
                                        </Link>
                                        <Link
                                            to="/dashboard/derivatives/history"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: "4px",
                                                border: "1px solid #EEEEEE",
                                                borderRadius: "12px",
                                                padding: "12px 8px",
                                                backgroundColor: COLORS.WHITE,
                                                cursor: "pointer",
                                                width: isMd ? "100px" : "100%",
                                            }}
                                        >
                                            <Typography sx={{ fontSize: "10px", fontWeight: 900, color: COLORS.BLACK }}>
                                                تاریخچه
                                            </Typography>
                                            <HistoryIcon sx={{ color: COLORS.BLACK }} />
                                        </Link>
                                    </Box>
                                </Grid2>
                            </Grid2>
                        </Card>
                    </Grid2>

                    {/* table */}
                    <Grid2 xs={12}>
                        <DerivativesStrategiesTable />
                    </Grid2>
                </Grid2>
            </Paper>

            {/* details and charts */}
            <Paper>
                <Grid2 container spacing={2}>
                    {/* details */}
                    <Grid2 xs={12}>
                        {selectedRowDetails?.actions?.length ? (
                            <Paper>
                                <Typography
                                    sx={{
                                        color: COLORS.BLACK,
                                        fontSize: "16px",
                                        fontWeight: 700,
                                        marginBottom: "14px",
                                    }}
                                >
                                    نحوه ورود به معامله
                                </Typography>
                                <Grid2 container spacing="20px">
                                    {selectedRowDetails?.actions?.map((action, index) =>
                                        action.action === "خرید" ? (
                                            <DerivativesStrategiesDetailsAction
                                                key={index}
                                                title="خرید"
                                                borderColor="#C8E6C9"
                                                symbol={action.symbol}
                                                icon={<TrendingUpIcon sx={{ color: COLORS.GREEN }} />}
                                                link={action.link}
                                                price={action.price}
                                                lastUpdate={action.last_update}
                                                orderBookTableData={action.order_book}
                                            />
                                        ) : (
                                            <DerivativesStrategiesDetailsAction
                                                key={index}
                                                title="فروش"
                                                borderColor="#FFCDD2"
                                                symbol={action.symbol}
                                                icon={<TrendingDownIcon sx={{ color: COLORS.RED }} />}
                                                link={action.link}
                                                price={action.price}
                                                lastUpdate={action.last_update}
                                                orderBookTableData={action.order_book}
                                            />
                                        )
                                    )}
                                </Grid2>
                            </Paper>
                        ) : (
                            <Card>
                                <Box
                                    sx={{
                                        width: "100%",
                                        height: "356px",
                                        backgroundColor: "#FFFFFF33",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: "12px",
                                    }}
                                >
                                    <Typography
                                        sx={{ fontSize: "24px", fontWeight: 900, color: COLORS.BLACK, width: "236px" }}
                                    >
                                        برای دیدن جزئیات ابتدا یک نماد را انتخاب کنید
                                    </Typography>
                                </Box>
                            </Card>
                        )}
                    </Grid2>

                    {/* charts */}
                    {selectedRowDetails?.actions?.length && (
                        <Grid2 xs={12}>
                            <Paper>
                                <Typography
                                    sx={{
                                        color: COLORS.BLACK,
                                        fontSize: "16px",
                                        fontWeight: 700,
                                        marginBottom: "14px",
                                    }}
                                >
                                    نمودارها
                                </Typography>
                                <Card sx={{ padding: "20px" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "18px", fontWeight: 400, color: COLORS.BLACK }}>
                                            دقت نمودار
                                        </Typography>
                                        <Slider
                                            value={chartAccuracy}
                                            onChange={handleSliderChange}
                                            step={10}
                                            min={10}
                                            max={50}
                                            marks={[
                                                { value: 10, label: "10" },
                                                { value: 20, label: "20" },
                                                { value: 30, label: "30" },
                                                { value: 40, label: "40" },
                                                { value: 50, label: "50" },
                                            ]}
                                            sx={{
                                                width: "300px",
                                                "& .MuiSlider-thumb": { backgroundColor: COLORS.BLUE },
                                                "& .MuiSlider-track": { backgroundColor: COLORS.BLUE },
                                                "& .MuiSlider-rail": { backgroundColor: "#E0E0E0", height: "8px" },
                                                "& .MuiSlider-markLabel": {
                                                    color: COLORS.GRAY,
                                                    fontSize: "14px",
                                                    fontWeight: 900,
                                                },
                                                "& .MuiSlider-markLabelActive": { color: COLORS.GRAY },
                                            }}
                                        />
                                    </Box>
                                    <Grid2 container spacing={5}>
                                        <DerivativesStrategiesDetailsChart
                                            title="نمودار سود بر حسب قیمت"
                                            data={chartData.priceChartData}
                                            baseEquidityLastPrice={selectedRowDetails.base_equity_last_price}
                                        />
                                        <DerivativesStrategiesDetailsChart
                                            title="نمودار سود بر حسب درصد"
                                            data={chartData.percentChartData}
                                            baseEquidityLastPrice={selectedRowDetails.base_equity_last_price}
                                        />
                                    </Grid2>
                                </Card>
                            </Paper>
                        </Grid2>
                    )}
                </Grid2>
            </Paper>
        </Box>
    );
};

export default DerivativesStrategies;